import { useState } from "react";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import { useUploadPhotosContext } from "components/form/uploadPhotos/UploadPhotos";
import PhotoCropper from "components/photoCropper/PhotoCropper";
import { useGetProduct } from "queries/legacy/product-queries";
import { UploadedPhoto } from "types/schema";
import { SSR_CONFIGURATION_ERROR } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";

import Modal, { ModalProps } from "../Modal";

import styles from "./PhotoEditModal.module.scss";

export type PhotoEdits = Pick<UploadedPhoto, "crop" | "filter">;

type PhotoEditModalProps = Omit<ModalProps, "children"> & {
  onEditComplete: (edits: PhotoEdits) => void;
};

/**
 * Modal for cropping and applying filters to user-uploaded photos.
 *
 * Uses PhotoCropper to crop
 */
export default function PhotoEditModal({
  onEditComplete,
  ...modalProps
}: PhotoEditModalProps) {
  const { data: product } = useGetProduct();

  const interpolate = useInterpolate();

  const { photo } = useUploadPhotosContext();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  if (!photo) {
    throw new Error("Photo edit modal called without a photo");
  }

  const [edits, setEdits] = useState<PhotoEdits>({
    crop: photo.crop,
    filter: photo.filter,
  });

  return (
    <Modal className={styles.modal} {...modalProps}>
      <div className={styles.modalInner}>
        <PhotoCropper
          cropData={edits.crop}
          onCrop={(crop) => setEdits({ ...edits, crop })}
        />

        <div className={styles.cta}>
          <PrimaryButton onClick={() => onEditComplete(edits)}>
            {interpolate(
              product.content.canvas.form_submit_button_label ??
                CANVAS_FALLBACK_CONTENT.form_submit_button_label
            )}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
}
